import React from "react";
import videojs from "video.js";
import type Player from "video.js/dist/types/player";
import appLogo from "assets/img/layout/easyfollow-logo.png";
import "video.js/dist/video-js.css";
import "./videojs-theme-easyfollow.css";
import {
  addSkipSeekButtonsToControlBar,
  registerVideoJsSkipSeekButtons,
} from "./videojsSkipSeekButtons";
import {
  addSpeedCycleButtonToControlBar,
  registerVideoJsSpeedCycleButton,
} from "./videojsSpeedCycleButton";

registerVideoJsSkipSeekButtons();
registerVideoJsSpeedCycleButton();

export type PlyrVideoPlayerProps = {
  src: string;
  mimeType?: string;
  className?: string;
  /** Short label under / beside logo (e.g. product name). */
  brandName?: string;
  /** Monogram when `logoSrc` is not set. */
  brandInitial?: string;
  /** Logo URL; defaults to bundled Easyfollow mark. Pass `null` to use monogram only. */
  logoSrc?: string | null;
  /** Hide the top brand overlay. */
  hideBrandBadge?: boolean;
};

function VideoJsInner({ src }: { src: string }) {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const playerRef = React.useRef<Player | null>(null);

  React.useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const player = videojs(el, {
      controls: true,
      fluid: true,
      // Responsive layout rebuilds the control bar and drops custom children; keep skip buttons stable.
      responsive: false,
      playbackRates: [0.75, 1, 1.25, 1.5, 1.75, 2],
      controlBar: {
        pictureInPictureToggle: true,
        volumePanel: { inline: true },
      },
    });

    playerRef.current = player;
    addSkipSeekButtonsToControlBar(player);
    addSpeedCycleButtonToControlBar(player);
    // String URL only: rely on server Content-Type + browser sniffing. Forcing e.g.
    // video/quicktime often makes Video.js report "No compatible source" in Chrome
    // even when the file plays in a new tab. Omit crossOrigin so R2 presigned URLs
    // work without video CORS headers.
    player.src(src);

    return () => {
      player.dispose();
      playerRef.current = null;
    };
    // Parent remounts with key={src}; re-running would double-init Video.js on the same node.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <video
      ref={videoRef}
      className="video-js vjs-big-play-centered vjs-easyfollow"
      playsInline
      preload="metadata"
    />
  );
}

/**
 * Video.js player — unified skin, playback speed, PiP, fullscreen (not the browser default bar).
 */
export default function PlyrVideoPlayer({
  src,
  className = "",
  brandName,
  brandInitial = "E",
  logoSrc = appLogo,
  hideBrandBadge = false,
}: PlyrVideoPlayerProps) {
  const initial = (brandInitial || "E").slice(0, 1).toUpperCase();
  const resolvedLogo = logoSrc === null ? null : logoSrc || appLogo;

  return (
    <div
      className={`vjs-easyfollow-shell group/player relative overflow-hidden rounded-xl bg-gradient-to-b from-[#1E4D2B]/[0.12] via-gray-950/[0.04] to-gray-950/[0.08] ring-1 ring-[#1E4D2B]/25 dark:from-[#1E4D2B]/20 dark:via-black/45 dark:to-black/55 dark:ring-[#2D5A3E]/40 ${className}`}
    >
      {!hideBrandBadge && (
        <div
          className="pointer-events-none absolute start-4 top-4 z-20 flex max-w-[min(168px,46%)] items-center gap-1.5 rounded-2xl border border-white/50 bg-white/90 px-2 py-1 shadow-[0_8px_24px_rgba(0,0,0,0.18)] backdrop-blur-md dark:border-white/10 dark:bg-[#0B1220]/80 dark:shadow-black/40"
          aria-hidden
        >
          {resolvedLogo ? (
            <img
              src={resolvedLogo}
              alt=""
              className="h-6 w-6 shrink-0 rounded-md object-contain"
            />
          ) : (
            <span
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#1E4D2B] text-[10px] font-bold text-[#F5E6B8] ring-1 ring-white/20"
              aria-hidden
            >
              {initial}
            </span>
          )}
          {brandName ? (
            <span className="truncate pe-0.5 text-[11px] font-bold leading-none tracking-tight text-[#1A3D28] dark:text-[#D5F0E2]">
              {brandName}
            </span>
          ) : null}
        </div>
      )}
      <VideoJsInner key={src} src={src} />
    </div>
  );
}
